import {
  CarbonIntensity,
  CarbonIntensities
} from '@/models/carbon/CarbonIntensity.js'
import {
  ImpactIntensity,
  ImpactIntensities
} from '@/models/carbon/ImpactIntensity.js'
import PURCHASES_FACTORS from '@/../data/factors/carbon/purchasesFactors.json'
import PURCHASES_LCA_FACTORS from '@/../data/factors/lca/purchasesFactors.json'
import Model from '@/models/carbon/Model.js'
import Modules from '@/models/Modules.js'
import { EmissionFactor, ImpactFactor } from '@/models/carbon/Factor.js'
import { intConverter, floatConverter, tagsConverter } from '@/utils/parser.js'

const TRAVELLING_EXPENSES_CODES = ['xa01', 'xa02', 'xa11', 'xa12']
const WARNING_RATE = 20

export default class Purchase extends Model {
  constructor ({
    id = null,
    code = '',
    amount = 0,
    amortization = 1,
    info = '',
    tags = [],
    source = null
  } = {}) {
    super({ tags, source })
    this.id = id
    this.setCode(code)
    this.amount = this.setAmount(amount)
    this.amortization = amortization
    this.info = info
    this.intensity = new CarbonIntensity()
    this.impact = new ImpactIntensity()
  }

  descriptor () {
    return this.code
  }

  isSimilarTo (other) {
    let r =
      this === other || // same ref
      this.uuid === other.uuid // the purchase has been copied
    return r
  }

  get score () {
    if (
      !isNaN(this.amount) &&
      Object.keys(PURCHASES_FACTORS).includes(this.code)
    ) {
      // valid
      return 1
    }
    // invalid
    return 2
  }

  get module () {
    if (Object.keys(PURCHASES_FACTORS).includes(this.code)) {
      return PURCHASES_FACTORS[this.code].module
    } else {
      return null
    }
  }

  get category () {
    if (Object.keys(PURCHASES_FACTORS).includes(this.code)) {
      return PURCHASES_FACTORS[this.code].category
    } else {
      return null
    }
  }

  get categoryGHGP () {
    let catGHGP = '3.1'
    if (Object.keys(PURCHASES_FACTORS).includes(this.code)) {
      if (PURCHASES_FACTORS[this.code].catGHGP !== null) {
        catGHGP = PURCHASES_FACTORS[this.code].catGHGP
      }
      if (this.isAmortized) {
        catGHGP = '3.2'
      }
    }
    return catGHGP
  }

  get categoryV5 () {
    let catV5 = '4.1'
    if (Object.keys(PURCHASES_FACTORS).includes(this.code)) {
      if (PURCHASES_FACTORS[this.code].catV5 !== null) {
        catV5 = PURCHASES_FACTORS[this.code].catV5
      }
      if (this.isAmortized) {
        catV5 = '4.2'
      }
    }
    return catV5
  }

  get description () {
    if (Object.keys(PURCHASES_FACTORS).includes(this.code)) {
      return PURCHASES_FACTORS[this.code].description_FR
    } else {
      return null
    }
  }

  clean () {
    // coerce to float amount
    // (input form might set this as string)
    this.amount = parseFloat(this.amount)
  }

  toString (sep = '\t') {
    let values = [
      this.code.toUpperCase(),
      this.info,
      this.module,
      this.categoryGHGP,
      this.categoryV5,
      this.category,
      this.description,
      this.amount,
      this.tags.map((val) => val.name).join(';'),
      this.amortization,
      Math.round(this.intensity.intensity),
      Math.round(this.intensity.uncertainty)
    ]
    for (let key of ImpactFactor.keys) {
      if (Object.keys(this.impact.intensity).includes(key)) {
        values.push(this.impact.intensity[key])
      } else {
        values.push(0)
      }
    }
    return values.join(sep)
  }

  isOther () {
    return this.module !== null && this.module !== Purchase.MODULE_NAME
  }

  isValid () {
    return this.score === 1
  }

  isIncomplete () {
    return false
  }

  isInvalid () {
    return this.score === 2
  }

  get isAmortized () {
    return this.amortization > 1
  }

  setCode (value) {
    let lvalue = value.toLowerCase().replace('.', '').trim()
    this.code = lvalue
  }

  setAmount (value) {
    let amount = value
    if (typeof amount === 'string') {
      amount = amount.replace(',', '.').trim().replace(/\s/g, '')
    }
    if (!isNaN(amount)) {
      amount = parseFloat(amount)
    }
    return amount
  }

  getCarbonIntensity (year = null) {
    let ef = EmissionFactor.createFromObj(PURCHASES_FACTORS[this.code])
    ef = ef.getFactor(year)
    let intensity = new CarbonIntensity(
      (this.amount * ef.total.total) / this.amortization,
      ((this.amount * ef.total.total) / this.amortization) *
        ef.total.uncertainty,
      ef.group
    )
    this.intensity = intensity
    return intensity
  }

  getImpact (year = null) {
    let ifactors = ImpactFactor.createFromObj(PURCHASES_LCA_FACTORS[this.code])
    let impact = ImpactIntensity.compute(this.amount, ifactors)
    this.impact = impact
    return impact
  }

  getFactorDescription () {
    if (Object.keys(PURCHASES_FACTORS).includes(this.code)) {
      return PURCHASES_FACTORS[this.code].description_FR
    } else {
      return '-'
    }
  }

  getCodeFromDescription (value) {
    let lvalue = value.toLowerCase()
    let fcode = null
    for (let code of Object.keys(PURCHASES_FACTORS)) {
      if (PURCHASES_FACTORS[code].description_FR.toLowerCase() === lvalue) {
        fcode = code
      }
    }
    return fcode
  }

  toDatabase () {
    return {
      code: this.code,
      amount: this.amount,
      amortization: this.amortization,
      info: this.info,
      tags: this.tags
    }
  }

  static get formatDescription () {
    return {
      code: {
        pattern: /(code|nacre|code.nacre|codenacre|nacrecode)/i,
        converter: function (value) {
          return value.toLowerCase().replace('.', '')
        },
        required: true,
        default: '' // considered as invalid
      },
      amount: {
        pattern: /(montant|amount|somme|total|€)/i,
        converter: floatConverter,
        required: true,
        default: null // considered as invalid
      },
      amortization: {
        pattern: /(amortissement|amortization)/i,
        converter: intConverter,
        required: false,
        default: 1
      },
      info: {
        pattern: /(informations?|infos?)/i,
        required: false,
        default: ''
      },
      tags: {
        pattern: /(tags?|labels?)/i,
        required: false,
        converter: tagsConverter,
        default: []
      }
    }
  }

  static isACode (code) {
    let regex = new RegExp('^[a-z]{2}[0-9]{2}$')
    return regex.test(code.toLowerCase())
  }

  aggregateDescriptor () {
    return [this.code, this.info, this.amortization, this.tagsHash].join('_')
  }

  _doAggregate (other) {
    let newPurchase = Purchase.createFromObj(this)
    newPurchase.amount += other.amount
    return newPurchase
  }

  static getTravellingExpensesCodes () {
    return TRAVELLING_EXPENSES_CODES
  }

  static getWarningRate () {
    return WARNING_RATE
  }

  static getCatGHGP () {
    let catGHGP = Object.keys(PURCHASES_FACTORS).map(function (code) {
      return PURCHASES_FACTORS[code].catGHGP
    })
    return Array.from(new Set(catGHGP)).filter((val) => val !== null)
  }

  static getCatV5 () {
    let catV5 = Object.keys(PURCHASES_FACTORS).map(function (code) {
      return PURCHASES_FACTORS[code].catV5
    })
    return Array.from(new Set(catV5)).filter((val) => val !== null)
  }

  static getCategories () {
    let categories = Object.keys(PURCHASES_FACTORS).map(function (code) {
      return PURCHASES_FACTORS[code].category
    })
    return Array.from(new Set(categories)).filter((val) => val !== null)
  }

  static getCodes () {
    let codes = Object.keys(PURCHASES_FACTORS)
    return Array.from(new Set(codes))
  }

  static getDescriptions () {
    let descriptions = Object.keys(PURCHASES_FACTORS).map(function (code) {
      return PURCHASES_FACTORS[code].description_FR
    })
    return Array.from(new Set(descriptions))
  }

  static getDescription (code) {
    if (Object.keys(PURCHASES_FACTORS).includes(code)) {
      return PURCHASES_FACTORS[code].description_FR
    } else {
      return null
    }
  }

  static get MODULE_NAME () {
    return Modules.PURCHASES
  }

  static createFromObj (item) {
    // might pass item
    return new Purchase({
      id: item.id,
      code: item.code,
      amount: item.amount,
      amortization: item.amortization,
      info: item.info,
      tags: item.tags,
      source: item.source
    })
  }

  /**
   * @param {Object[]} lines - The list of purchases object to handle
   * @param {String} code - The NACRES code
   * @param {String} amortization - The amortization period
   * @param {String} info - Any infomration on the purchase (mainly usefull for amortized ones)
   * @param {String} tags - The list of tags defined by the user
   * @param {String} source - The source file name
   *
   * @returns {Purchase[]} - The list of purchase objects
   */
  static createFromLines (lines, format, tags, source) {
    let purchases = []
    for (let line of lines) {
      let nitem = Purchase.createFromObj({
        id: null,
        code: line.code,
        amount: line.amount,
        amortization: line.amortization,
        info: line.info,
        source: source
      })
      nitem.addTags(Model.getTagsFromNames(line.tags, tags))
      purchases.push(nitem)
    }
    return purchases
  }

  static exportHeader (sep = '\t') {
    let values = [
      'code',
      'info',
      'module',
      'ghg.source',
      'v5scope.source',
      'category',
      'description',
      'amount.euro',
      'tags',
      'amortization',
      'emission.kg.co2e',
      'uncertainty.kg.co2e'
    ]
    for (let key of ImpactFactor.keys) {
      values.push(key)
    }
    return values.join(sep)
  }

  static exportToFile (items, header = true, extraColValue = null, sep = '\t') {
    let headerValues = []
    if (header) {
      headerValues = Purchase.exportHeader(sep)
    }
    return [
      headerValues,
      ...items.map(function (item) {
        let val = ''
        if (extraColValue) {
          val += extraColValue + sep
        }
        return val + item.toString(sep)
      })
    ]
      .join('\n')
      .replace(/(^\[)|(\]$)/gm, '')
  }

  static compute (items, year, tags = []) {
    let intensitiesAmort = new CarbonIntensities()
    let intensitiesNonAmort = new CarbonIntensities()
    let intensitiesTotal = new CarbonIntensities()
    let impacts = new ImpactIntensities()
    let catGHGP = {}
    let catV5 = {}
    let categories = {}
    let totalXA = 0
    for (let item of items.filter((obj) => obj.hasTags(tags))) {
      let intensity = item.getCarbonIntensity(year)
      let impact = item.getImpact(year)
      if (item.module === Purchase.MODULE_NAME) {
        if (item.isAmortized) {
          intensitiesAmort.add(intensity)
        } else {
          intensitiesNonAmort.add(intensity)
        }
        intensitiesTotal.add(intensity)
        impacts.add(impact)
        if (Object.keys(catGHGP).includes(item.categoryGHGP.toString())) {
          catGHGP[item.categoryGHGP.toString()].add(intensity)
        } else {
          catGHGP[item.categoryGHGP.toString()] = new CarbonIntensities()
          catGHGP[item.categoryGHGP.toString()].add(intensity)
        }
        if (Object.keys(catV5).includes(item.categoryV5.toString())) {
          catV5[item.categoryV5.toString()].add(intensity)
        } else {
          catV5[item.categoryV5.toString()] = new CarbonIntensities()
          catV5[item.categoryV5.toString()].add(intensity)
        }

        if (!Object.keys(categories).includes(item.category)) {
          categories[item.category] = {
            nonAmort: new CarbonIntensities(),
            amort: new CarbonIntensities(),
            total: new CarbonIntensities()
          }
        }
        categories[item.category]['total'].add(intensity)
        if (item.isAmortized) {
          categories[item.category]['amort'].add(intensity)
        } else {
          categories[item.category]['nonAmort'].add(intensity)
        }

        if (TRAVELLING_EXPENSES_CODES.includes(item.code)) {
          totalXA += intensity.intensity
        }
      }
    }

    Object.keys(categories).map(function (key) {
      categories[key]['total'] = categories[key]['total'].sum()
      categories[key]['amort'] = categories[key]['amort'].sum()
      categories[key]['nonAmort'] = categories[key]['nonAmort'].sum()
    })
    Object.keys(catGHGP).map(function (key) {
      catGHGP[key] = catGHGP[key].sum()
    })
    Object.keys(catV5).map(function (key) {
      catV5[key] = catV5[key].sum()
    })
    return {
      intensity: intensitiesTotal.sum(),
      amort: intensitiesAmort.sum(),
      nonamort: intensitiesNonAmort.sum(),
      impact: impacts.sum(),
      catGHGP: catGHGP,
      catV5: catV5,
      categories: categories,
      totalXA: totalXA
    }
  }
}
